/*
 * Copyright (c) Nexign, JSC, 2021.
 */
/**
 * AuthForm for Service Access User - step two
 *
 * @author Yakov Gushchin
 * @version 1.11.0
 * @since 1.11.0
 */

import { ChangeEvent, FormEvent, useCallback, useState } from 'react';

import { browserName, deviceType } from 'react-device-detect';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import { createStructuredSelector } from 'reselect';

import { deviceTypes } from '@pages/PageAuth/constants/authConstants';

import { people } from '../../../assets/icons';
import { BlockWithIcon, Button, Input } from '../../../components/common';
import { EInputKind } from '../../../components/common/Input/Input';
import { makeSelectTranslationPageAuthServiceAccess } from '../../../modules/internationalization/intSelectors';
import { IPageAuthServiceAccessTranslation } from '../../../modules/internationalization/types';
import { closeNotificationAction, openNotification } from '../../../modules/notification/notificationActions';
import {
    ENotificationLevelTypes,
    ENotificationTypes,
    INotification,
} from '../../../modules/notification/types/notificationTypes';
import { IRootReducer } from '../../../reducers/rootReducer';
import { loginUserServiceAccessAction } from '../../PageAuth/actions/authServiceAccessActions';
import {
    FormContainerStyled,
    FormGroupStyled,
    FormSubtitleStyled,
    FormTitleStyled,
    FormWrapperStyled,
} from '../../PageAuth/styled/PageAuthStyled';
import { ILoginServiceAccessPayload } from '../../PageAuth/types/authServiceAccessTypes';

type TAuthFormServiceAccessMobile = IConnectedProps & IDispatchProps;

const AuthFormServiceAccessMobile = (props: TAuthFormServiceAccessMobile) => {

    const {
        translationServiceAccess,
        // handlers
        closeNotice,
        loginUserServiceAccess,
        openNotice,
    } = props;

    const userAgent = browserName;
    const typeDevice = deviceTypes[deviceType] ?? deviceTypes.unknown;

    const [login, setLogin] = useState('');
    const [validForm, setValidForm] = useState(true);

    const onInputChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
        setLogin(event.target.value);
    }, []);

    const submitHandler = useCallback((event: FormEvent) => {
        event.preventDefault();
        if (login) {
            loginUserServiceAccess({ username: login.trim(), userAgent, typeDevice })
            setValidForm(true);
            closeNotice();
        } else {
            openNotice({
                message: translationServiceAccess.notice.errorLoginUserForm,
                type: ENotificationTypes.AUTH,
                level: ENotificationLevelTypes.ERROR,
            });
            setValidForm(false);
        }
    }, [typeDevice, userAgent, closeNotice, login, loginUserServiceAccess, openNotice, translationServiceAccess]);

    return (
        <FormContainerStyled>
            <FormWrapperStyled onSubmit={submitHandler}>
                <FormTitleStyled>{ translationServiceAccess.userForm.title }</FormTitleStyled>
                <FormSubtitleStyled>{ translationServiceAccess.userForm.subTitle }</FormSubtitleStyled>
                <FormGroupStyled className="form-group__login-input">
                    <Input
                        data-input-login="loginAuthform"
                        kind={validForm ? EInputKind.normal : EInputKind.error}
                        label={translationServiceAccess.userForm.labelLogin}
                        name="username"
                        onChange={onInputChange}
                    />
                </FormGroupStyled>
                <FormGroupStyled style={{ paddingTop: '10px' }}>
                    <Button dataName="buttonSubmitAuthform" kind="medium">
                        { translationServiceAccess.userForm.buttonSubmit }
                    </Button>
                </FormGroupStyled>
            </FormWrapperStyled>
            <BlockWithIcon
                data-text-with-icon="howToGetAccess"
                iconRight={people}
                iconRightSize={64}
                justifyContent="flex-end"
            />
        </FormContainerStyled>
    );
};

interface IConnectedProps {
    translationServiceAccess: IPageAuthServiceAccessTranslation;
}

const mapStateToProps = createStructuredSelector<IRootReducer, IConnectedProps>({
    translationServiceAccess: makeSelectTranslationPageAuthServiceAccess(),
})

interface IDispatchProps {
    closeNotice: () => void;
    loginUserServiceAccess: (payload: ILoginServiceAccessPayload) => void;
    openNotice: ({ message, type }: INotification) => void;
}

export const mapDispatchToProps = (dispatch: Dispatch): IDispatchProps => ({
    closeNotice: () => dispatch(closeNotificationAction()),
    loginUserServiceAccess: (payload: ILoginServiceAccessPayload) => dispatch(loginUserServiceAccessAction(payload)),
    openNotice: (payload: INotification) => dispatch(openNotification(payload)),
});

export default connect(mapStateToProps, mapDispatchToProps)(AuthFormServiceAccessMobile);
